"use client";
import { Badge, Button, Dropdown, Skeleton, Slider, Typography } from "antd/lib";
import React, { useMemo, useState } from "react";
import { AlertOutlined, DownOutlined, ShareAltOutlined } from "@ant-design/icons";
import ellipsize from "ellipsize";
import dayjs from "dayjs";
import { useTranslations } from "next-intl";
import useAxiosQuery from "@/hooks/useMyQuery";
import type { IProductDetail, ProductPriceHeader } from "@/models/product";
import MyCard from "@/app/components/MyCard";
import PriceChange from "@/app/components/PriceChange";
import { TextNumberChange, TitleNumberChange } from "@/app/components/numberChange";
import { PercentChange } from "@/app/components/percentChange";
import { capitalizeEveryFirstCharacter } from "@/helpers";
import type { DeclareIcon } from "@/app/components/faIcon";
import { FaIcon } from "@/app/components/faIcon";
import LastUpdated from "@/app/components/LastUpdated";
import dynamic from "next/dynamic";
import { usePrice } from "@/context/PriceContext";
import { useCurrencyContext } from "@/context/CurrencyContext";
import { useShareModalContext } from "@/context/ShareContext";

const RankLabel = dynamic(() => import("@/app/components/ProductRank"), { ssr: false });

const iconDisplayCount = 4;
function ProductHeader({
  symbol,
  productDetail,
  activeTab,
}: {
  symbol: string | undefined;
  productDetail?: IProductDetail;
  activeTab: string;
}) {
  const t = useTranslations("products");
  const { getPrice } = usePrice();
  const { currentCurrency } = useCurrencyContext();
  const [marketDexQuery, switchMarketDexQuery] = useState<string>("prod_market_dex");
  const { showModal } = useShareModalContext();
  const { data, fetchTime: lastUpdate } = useAxiosQuery<{ data: ProductPriceHeader[] }>({
    url: "v1/product/overview/price-product-header",
    method: "get",
    params: {
      symbol,
    },
  });

  const marketDropdownItems = useMemo(() => data?.data ?? [], [data?.data]);

  const marketDex = useMemo(() => {
    if (marketDropdownItems.length) {
      return (
        marketDropdownItems.find(m => m.exchangeName === marketDexQuery) ||
        marketDropdownItems.find(m => m.exchangeName?.toLowerCase()?.includes("klarda")) ||
        marketDropdownItems[0]
      );
    }
  }, [marketDropdownItems, marketDexQuery]);

  const price = useMemo(
    () => getPrice(symbol ?? "")?.price ?? (marketDex?.last || 0),
    [getPrice, marketDex?.last, symbol],
  );
  const priceOpen = useMemo(() => marketDex?.open || 0, [marketDex]);
  const priceHigh = useMemo(() => marketDex?.high || 0, [marketDex]);
  const priceLow = useMemo(() => marketDex?.low || 0, [marketDex]);

  const priceChange = useMemo(() => (price - priceOpen) / (priceOpen || 1), [price, priceOpen]);
  const isPriceIncrease = useMemo(() => priceChange >= 0, [priceChange]);

  const lastPriceChange = useMemo(() => (price - priceLow) / (priceHigh - priceLow || 1), [price, priceHigh, priceLow]);
  const openPriceChange = useMemo(
    () => (priceOpen - priceLow) / (priceHigh - priceLow || 1),
    [priceHigh, priceLow, priceOpen],
  );

  const stokeProps = useMemo(() => {
    const color = priceChange >= 0 ? "#52c41a" : "#ff4d4f";

    const leftChange = Math.max(Math.round((isPriceIncrease ? openPriceChange : lastPriceChange) * 100), 0);
    const rightChange = Math.min(Math.round((isPriceIncrease ? lastPriceChange : openPriceChange) * 100), 100);

    return {
      color,
      leftChange,
      rightChange,
    };
  }, [isPriceIncrease, lastPriceChange, openPriceChange, priceChange]);

  const medias = useMemo(() => productDetail?.medias, [productDetail?.medias]);

  const displayMedias = useMemo(() => {
    if (iconDisplayCount <= 0) {
      return [];
    }
    return medias?.slice(0, iconDisplayCount) ?? [];
  }, [medias]);

  const moreMedias = useMemo(() => {
    if (iconDisplayCount <= 0) {
      return [];
    }
    return medias?.slice(iconDisplayCount) ?? [];
  }, [medias]);

  if (!productDetail) return <Skeleton active paragraph={{ rows: 6 }} />;
  return (
    <>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="w-full xl:pr-3 xl:border-r xl:border-solid xl:border-slate-100">
          <div className="flex justify-between w-full items-center flex-wrap">
            <div className="flex gap-4 flex-col">
              <div className="flex flex-wrap items-start gap-3">
                <div className="flex items-center gap-1">
                  <img alt={productDetail?.name || ""} src={productDetail?.logo_uri || ""} height={46} width={46} />
                  <Typography.Title level={3} className="m-0">
                    {productDetail?.name}
                  </Typography.Title>
                </div>
                <div className="mt-3 flex items-center gap-1">
                  <Typography.Text type="secondary" className="whitespace-nowrap">
                    {productDetail?.token_symbol}
                  </Typography.Text>
                  <RankLabel label={productDetail?.rank_label || ""} />
                </div>
              </div>
              <div className="flex gap-3 items-center">
                <TitleNumberChange value={price} level={1} className="m-0" prefix={currentCurrency} />
                <PercentChange change={priceChange} className="text-sm lg:text-xl" />
              </div>
            </div>
            <div className="flex flex-col gap-2 items-end">
              <div className="md:flex gap-2 hidden">
                <Button
                  icon={<AlertOutlined />}
                  href={`https://dashboard.klarda.com/product/${productDetail?.symbol}/overview`}
                />
                <Button
                  icon={<ShareAltOutlined />}
                  onClick={() =>
                    showModal({
                      featureName: `(${symbol}) ${activeTab}`,
                    })
                  }
                />
              </div>
              <div>
                {marketDropdownItems.length && (
                  <div className="h-8">
                    <Dropdown
                      menu={{
                        items: marketDropdownItems.map((m, index) => ({
                          key: index,
                          label: (
                            <div className="flex items-center gap-2">
                              <img alt={m?.exchangeIcon || ""} src={m?.exchangeIcon || ""} height={16} width={16} />
                              {m?.exchangeName}
                            </div>
                          ),
                          onClick: () => switchMarketDexQuery(m?.exchangeName),
                        })),
                      }}
                      trigger={["click"]}
                    >
                      <Button className="w-full">
                        <div className="flex justify-between w-full items-center">
                          <div className="flex items-center gap-2">
                            <Badge
                              status={"processing"}
                              color={
                                !!lastUpdate && lastUpdate.isAfter(dayjs().subtract(5, "minutes")) ? "#52c41a" : "#f00"
                              }
                            />
                            <img
                              alt={marketDex?.exchangeName || ""}
                              src={marketDex?.exchangeIcon || ""}
                              height={16}
                              width={16}
                            />
                            <Typography.Text className="hidden md:block">{marketDex?.exchangeName}</Typography.Text>
                          </div>
                          <DownOutlined />
                        </div>
                      </Button>
                    </Dropdown>
                  </div>
                )}
                {lastUpdate && <LastUpdated dateTime={lastUpdate} className="whitespace-nowrap" />}
              </div>
            </div>
          </div>
          <div>
            <Slider
              range
              value={[stokeProps.leftChange, stokeProps.rightChange]}
              disabled={false}
              tooltip={{
                formatter: value => {
                  if (value === stokeProps.leftChange) {
                    return priceChange > 0 ? t("Open_Price") : t("Latest_Price");
                  }
                  if (value === stokeProps.rightChange) {
                    return priceChange > 0 ? t("Latest_Price") : t("Open_Price");
                  }
                },
              }}
              styles={{ track: { background: stokeProps.color } }}
            />
            <div className="flex justify-between w-full flex-wrap">
              <div className="flex gap-2 items-center">
                <Typography.Text>{t("Low")}</Typography.Text>
                <TextNumberChange value={marketDex?.low || 0} prefix={currentCurrency} />
                <PriceChange price={marketDex?.low_percentage_diff ?? 0} />
              </div>
              <div className="flex gap-2 items-center">
                <Typography.Text>{t("Open")}</Typography.Text>
                <PriceChange price={marketDex?.open || 0} />
              </div>
              <div className="flex gap-2 items-center">
                <Typography.Text>{t("High")}</Typography.Text>
                <TextNumberChange value={marketDex?.high || 0} prefix={currentCurrency} />
                <PercentChange change={marketDex?.high_percentage_diff} isFullPercent={true} />
              </div>
            </div>
          </div>
        </div>
        <div className="w-full">
          <MyCard>
            <div className="flex justify-between w-full">
              <Typography.Title level={5} className="m-0">
                {t("About")} {productDetail.name}
              </Typography.Title>
              {/* <Button icon={<BugFilled />}>{t("Report_issue")}</Button> */}
            </div>
            <Typography.Text type="secondary">{ellipsize(productDetail.short_description || "", 120)}</Typography.Text>
            <div className="flex flex-col gap-2 mt-4">
              <Typography.Text>{t("Social_Media")}</Typography.Text>
              <div className="flex gap-2">
                {displayMedias.map(media => (
                  <Typography.Link
                    title={capitalizeEveryFirstCharacter(media.type)}
                    key={media.url + media.type}
                    type="secondary"
                    href={media.url}
                    target="_blank"
                  >
                    <FaIcon className="size-6 text-[#1D9BF0]" name={media.type as DeclareIcon} />
                  </Typography.Link>
                ))}
                {!!moreMedias.length && (
                  <Dropdown
                    menu={{
                      items: moreMedias.map(m => ({
                        key: m.type + m.url,
                        label: (
                          <Typography.Link key={m.type + m.url} href={m.url} target="_blank">
                            <div className="flex items-center gap-1">
                              <FaIcon className="size-6 text-[#1D9BF0]" name={m.type as DeclareIcon} />
                              <span className="capitalize">{m.type}</span>
                            </div>
                          </Typography.Link>
                        ),
                      })),
                    }}
                  >
                    <Button size="small">
                      <div className="flex items-center gap-2">
                        {`+${moreMedias.length}`}
                        <DownOutlined />
                      </div>
                    </Button>
                  </Dropdown>
                )}
              </div>
            </div>
          </MyCard>
        </div>
      </div>
    </>
  );
}

export default React.memo(ProductHeader);
